import './Reporting.scss';
import { useEffect, useMemo, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import { MdCheck, MdError, MdExpandLess, MdExpandMore, MdSearch } from 'react-icons/md';
import { ReportInfo } from '@blisspointmedia/bpm-types/dist/Reporting';
import { EmptyState } from '@Components/EmptyState';
import { FilterBar } from '@Components/FilterBar';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Appearance, Background, Design, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { ListGroupSkeleton, Skeleton } from '@Components/Skeleton';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useSetError } from '@src/redux/modals';
import { abbreviateNumber } from '@src/utils/data';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { useStateFunction } from '@src/utils/hooks/useData';
import { FILTER_OPTIONS } from './reporting-constants';
import { colorForStatus, computeVisibleLines, healthColor, toPrettyDate } from './reporting-utils';
import { useReporting } from './use-reporting';

export const Reporting = () => {
  const company = useLocationCompany();

  const { data, isLoading: isReportingLoading, error } = useReporting(company ?? '');
  // useReporting is disabled (and its own isLoading is false) until useLocationCompany resolves
  // a company from the URL, so fold that "not resolved yet" state into loading too - otherwise
  // the page briefly (or permanently, for a bad company slug) shows the empty state instead of
  // the skeleton before the company ever loads.
  const isLoading = isReportingLoading || company === null;
  const setError = useSetError();

  const [expandedRun, setExpandedRun] = useState<string>();
  const [expandedType, setExpandedType] = useState<string>();

  const [filter, setFilter] = useStateFunction<(row: ReportInfo) => boolean>(() => true);

  const visibleLines = useMemo(
    () => computeVisibleLines(data, filter, expandedType),
    [data, filter, expandedType]
  );

  useEffect(() => {
    if (error) {
      setError({
        message: `Can't fetch reporting info. Error: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page title='Reporting' pageType='Reporting' isLoading={isLoading}>
      <div className='reporting-page'>
        <WidgetContainer
          className='reporting-widget'
          background={Background.DARK}
          fillHeight
          header={
            <div className='filter-bar' data-testid='reporting-filter-bar'>
              <FilterBar
                size={Size.LG}
                hasAdvanced
                options={FILTER_OPTIONS}
                lines={data ?? []}
                onFilter={setFilter}
              />
            </div>
          }
        >
          <ReportingBody
            isLoading={isLoading}
            data={data}
            visibleLines={visibleLines}
            expandedRun={expandedRun}
            expandedType={expandedType}
            setExpandedRun={setExpandedRun}
            setExpandedType={setExpandedType}
          />
        </WidgetContainer>
      </div>
    </Page>
  );
};

const ReportsPageSkeleton = () => (
  <Skeleton>
    <ListGroupSkeleton lineHeight={70} lineSpacing={10} />
  </Skeleton>
);

interface ReportDetailsProps {
  row: ReportInfo;
}

const ReportDetails = ({ row }: ReportDetailsProps) => (
  <div className='details' data-testid='report-details'>
    <div className='queries'>
      <div className='section-header'>
        Queries
        {Boolean(row.queries?.length) && (
          <CopyButton
            stopPropagation
            text={row.queries.join('\n')}
            data-testid='copy-queries-button'
          />
        )}
      </div>
      <div className='section-header'>Redshift Tables</div>
      {row.tables?.length
        ? row.tables.map(table => (
            <div key={table} className='file-info'>
              {table}
            </div>
          ))
        : 'Reporting tables unavailable'}
    </div>
    <div className='files'>
      <div className='section-header'>Files</div>
      {row.files?.length
        ? row.files.map(file => (
            <div
              key={file.path}
              className='file-info'
              style={{ backgroundColor: file.is_too_small ? healthColor(true) : 'transparent' }}
            >
              {file.path}{' '}
              <span className='success'>
                ({file.success ? <MdCheck /> : <MdError />} {abbreviateNumber(file.size)}b)
              </span>
            </div>
          ))
        : 'File info unavailable'}
    </div>
  </div>
);

// One-time column labels, matching the header-row-once shape every other table in the app uses
// (LinearCpms/DagGraphs/LinearOrders) instead of repeating a label inside every data row.
const ReportListHeader = () => (
  <div className='column-header'>
    <div className='expand' />
    <div className='name'>Name</div>
    <div className='lookback'>Lookback</div>
    <div className='status'>Status</div>
    <div className='date'>Started</div>
    <div className='date'>Finished</div>
    <div className='uploads'>Uploads</div>
  </div>
);

interface ReportRowProps {
  row: ReportInfo;
  isExpanded: boolean;
  isTypeExpanded: boolean;
  onToggleExpand: () => void;
  onToggleType: () => void;
}

const ReportRow = ({
  row,
  isExpanded,
  isTypeExpanded,
  onToggleExpand,
  onToggleType
}: ReportRowProps) => (
  <div className='list-item' data-testid={`report-row-${row.build_number}`}>
    <InteractiveDiv className='data-row' data-testid='row-toggle' onClick={onToggleExpand}>
      <div className='expand'>{isExpanded ? <MdExpandLess /> : <MdExpandMore />}</div>
      <div className='name'>
        <span>{row.name}</span>
        <IconButton
          size={Size.SM}
          appearance={Appearance.EMPTY}
          design={isTypeExpanded ? Design.BRAND : Design.NEUTRAL}
          className='expanded-type'
          data-testid='expand-type-button'
          onClick={e => {
            e.stopPropagation();
            onToggleType();
          }}
          tooltip={{
            text: isTypeExpanded ? 'Show latest run only' : `Show every run of ${row.name}`
          }}
        >
          <MdSearch />
        </IconButton>
      </div>
      <div className='lookback'>{row.lookback}</div>
      <div className='status'>
        <div className='status-value' style={{ backgroundColor: colorForStatus(row.status) }}>
          {row.status}
        </div>
      </div>
      <div className='date'>{toPrettyDate(row.created)}</div>
      <div className='date'>
        <div
          className='finished-value'
          style={{ backgroundColor: healthColor(Boolean(row.is_late)) }}
        >
          {row.completed ? toPrettyDate(row.completed) : 'n/a'}
        </div>
      </div>
      <div className='uploads'>
        <div
          className='uploads-value'
          style={{
            backgroundColor: healthColor(Boolean(row.files?.some(file => file.is_too_small)))
          }}
        >
          {row.files?.filter(file => file.success).length ?? 0} / {row.files?.length ?? 0} (
          {abbreviateNumber(
            row.files?.reduce((sum, file) => sum + (file.success ? file.size : 0), 0) ?? 0
          )}
          b)
        </div>
      </div>
    </InteractiveDiv>
    {isExpanded && <ReportDetails row={row} />}
  </div>
);

interface ReportListProps {
  visibleLines: ReportInfo[];
  expandedRun: string | undefined;
  expandedType: string | undefined;
  setExpandedRun: (value: string | undefined) => void;
  setExpandedType: (value: string | undefined) => void;
}

const ReportList = ({
  visibleLines,
  expandedRun,
  expandedType,
  setExpandedRun,
  setExpandedType
}: ReportListProps) => (
  <div className='list-container' data-testid='reporting-list'>
    <ReportListHeader />
    {visibleLines.map(row => (
      <ReportRow
        key={row.build_number}
        row={row}
        isExpanded={expandedRun === row.build_number}
        isTypeExpanded={expandedType === row.name}
        onToggleExpand={() =>
          setExpandedRun(expandedRun === row.build_number ? undefined : row.build_number)
        }
        onToggleType={() => setExpandedType(expandedType === row.name ? undefined : row.name)}
      />
    ))}
  </div>
);

interface ReportingBodyProps {
  isLoading: boolean;
  data: ReportInfo[] | undefined;
  visibleLines: ReportInfo[];
  expandedRun: string | undefined;
  expandedType: string | undefined;
  setExpandedRun: (value: string | undefined) => void;
  setExpandedType: (value: string | undefined) => void;
}

const ReportingBody = ({
  isLoading,
  data,
  visibleLines,
  expandedRun,
  expandedType,
  setExpandedRun,
  setExpandedType
}: ReportingBodyProps) => {
  if (isLoading) return <ReportsPageSkeleton />;
  if (!data || data.length === 0) return <EmptyState title='No Reports' />;

  return (
    <ReportList
      visibleLines={visibleLines}
      expandedRun={expandedRun}
      expandedType={expandedType}
      setExpandedRun={setExpandedRun}
      setExpandedType={setExpandedType}
    />
  );
};
